<script setup lang="ts">
import RcSiteSelect, { type ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import {
  enrollmentEndToDate,
  enrollmentStartToDate,
  formatEnrollmentDate,
  formatEnrollmentEndDate,
} from '@/helpers/eos/table-formatters'
import type {
  ICreateOptOutPeriodBody,
  IEditProgramEnrollmentBody,
  IISOEnergyProgram,
  IProgramEnrollment,
} from '@/types/eos/ProgramEnrollment'
import { Calendar, Delete } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import type { FormInstance, FormRules } from 'element-plus'
import { isNil } from 'lodash'
import { DateTime } from 'luxon'
import type { MaskInputOptions } from 'maska'
import { vMaska } from 'maska/vue'
import { computed, reactive, ref, watch } from 'vue'
import OptOutPeriodsList from './OptOutPeriodsList.vue'
import ProgramSelect from './ProgramSelect.vue'
import { useSites } from '@/composables/core/sites'

export interface ProgramEnrollmentEntry {
  program?: IISOEnergyProgram
  site_split?: string
  start_enrollment_period_timestamp?: string
  end_enrollment_period_timestamp?: string
  doesntEnd: boolean
  /** FE-only stable key */
  data_key: string
}

export interface ProgramEnrollmentFormValue {
  site?: ISiteIdentifier
  iso?: string
  enrollments: ProgramEnrollmentEntry[]
}

const model = defineModel<ProgramEnrollmentFormValue>({ required: true })

const props = defineProps<{
  disabled?: boolean
  existingEnrollments?: IProgramEnrollment[] | null
}>()

const emit = defineEmits<{
  'delete-existing': [payload: { id: number; onSuccess: () => void }]
  'edit-existing': [payload: { id: number; body: IEditProgramEnrollmentBody; onSuccess: () => void }]
  'create-opt-out-period': [
    payload: { programEnrollmentId: number; body: ICreateOptOutPeriodBody; onSuccess: () => void },
  ]
  'delete-opt-out-period': [
    payload: {
      programEnrollmentId: number
      optOutPeriodId: number
      onSuccess: () => void
      onError: (err: unknown) => void
    },
  ]
}>()

const isEditMode = computed(() => !isNil(props.existingEnrollments))

// We fetch the site's ISO upon site selection
const { siteDetailsQuery } = useSites()

const siteId = computed(() => model.value.site?.site_id)

const { data, isLoading, isError, error } = siteDetailsQuery(siteId)

watch(data, (newData) => {
  model.value.iso = newData?.iso
})

function entryIsValid(e: ProgramEnrollmentEntry) {
  if (!e.program) return false
  if (!e.site_split) return false
  if (!e.start_enrollment_period_timestamp) return false
  if (!e.doesntEnd && !e.end_enrollment_period_timestamp) return false
  if (e.end_enrollment_period_timestamp && e.end_enrollment_period_timestamp < e.start_enrollment_period_timestamp)
    return false
  return true
}

const isValid = computed(
  () =>
    !!model.value.iso &&
    !!model.value.site &&
    model.value.enrollments.length > 0 &&
    model.value.enrollments.every(entryIsValid)
)

defineExpose({ isValid, resetTransientState })

const enrollmentFormRefs = ref<FormInstance[]>([])

function getEnrollmentRules(entry: ProgramEnrollmentEntry): FormRules<ProgramEnrollmentEntry> {
  return {
    program: [{ required: true, message: 'Program is required', trigger: ['blur', 'change'] }],
    site_split: [
      {
        validator: (_rule, value: string | undefined, callback: (e?: Error) => void) => {
          if (!value) {
            callback(new Error('Required'))
            return
          }
          const num = parseInt(value, 10)
          if (isNaN(num) || num < 0 || num > 100) {
            callback(new Error('Must be 0–100'))
            return
          }
          callback()
        },
        trigger: ['blur', 'change'],
      },
    ],
    start_enrollment_period_timestamp: [
      { required: true, message: 'Effective date is required', trigger: ['blur', 'change'] },
    ],
    end_enrollment_period_timestamp: [
      {
        validator: (_rule, value: string | null, callback: (e?: Error) => void) => {
          if (!entry.doesntEnd) {
            if (!value) {
              callback(new Error('End date is required'))
              return
            }
            if (entry.start_enrollment_period_timestamp && value < entry.start_enrollment_period_timestamp) {
              callback(new Error('End date must be on or after effective date'))
              return
            }
          }
          callback()
        },
        trigger: ['blur', 'change'],
      },
    ],
  }
}

const siteSplitOptions: MaskInputOptions = {
  mask: '999',
  tokens: { '9': { pattern: /\d/, optional: true } },
  postProcess: (val) => {
    const num = parseInt(val, 10)
    return num > 100 ? '100' : val
  },
}

function describePeriod(entry: ProgramEnrollmentEntry): string | undefined {
  if (!entry.start_enrollment_period_timestamp) return undefined
  if (!entry.doesntEnd) {
    const end = entry.end_enrollment_period_timestamp
    if (!end || end < entry.start_enrollment_period_timestamp) return undefined
  }
  const fmt = (date: string) => DateTime.fromISO(date).toFormat('MM/dd/yyyy')
  const from = `Effective from ${fmt(entry.start_enrollment_period_timestamp)} 00:00`
  const to =
    !entry.doesntEnd && entry.end_enrollment_period_timestamp
      ? ` to ${fmt(entry.end_enrollment_period_timestamp)} 23:59`
      : ''
  const tz = data.value?.timezone
  return `${from}${to}${tz ? ` (${tz})` : ''}`
}

function newEntry(): ProgramEnrollmentEntry {
  return { data_key: crypto.randomUUID(), doesntEnd: true }
}

function removeEnrollment(index: number) {
  model.value.enrollments.splice(index, 1)
  enrollmentFormRefs.value.splice(index, 1)
}

// The end-date rule depends on the effective date, so re-check it when that changes.
// Skipped while the end date is still empty so we don't flag it before it's been touched.
function revalidateEndDate(form: FormInstance | undefined, entry: ProgramEnrollmentEntry) {
  if (entry.doesntEnd || !entry.end_enrollment_period_timestamp) return
  form?.validateField('end_enrollment_period_timestamp')
}

function toggleDoesntEnd(entry: ProgramEnrollmentEntry, index: number) {
  entry.doesntEnd = !entry.doesntEnd
  if (entry.doesntEnd) {
    entry.end_enrollment_period_timestamp = undefined
  }
  enrollmentFormRefs.value[index]?.validateField('end_enrollment_period_timestamp')
}

// --- Editing existing enrollments (period + split only; program is immutable) ---
const editingId = ref<number | null>(null)
const editEntry = ref<ProgramEnrollmentEntry | null>(null)
const editFormRef = ref<FormInstance>()
// When toggled, the card is marked for deletion: the form is disabled and the
// removal is only committed on "Delete".
const markedForDeletion = ref(false)

const isEditEntryValid = computed(() => !!editEntry.value && entryIsValid(editEntry.value))

// While marked for deletion the whole edit form is locked.
const editControlsDisabled = computed(() => props.disabled || markedForDeletion.value)

function startEdit(enrollment: IProgramEnrollment) {
  editingId.value = enrollment.id
  markedForDeletion.value = false
  editEntry.value = {
    data_key: crypto.randomUUID(),
    program: enrollment.program,
    site_split: parseFloat(enrollment.site_split).toString(),
    // The model stores full ISO timestamps; the date picker works in YYYY-MM-DD in the site's timezone,
    // which is also what the API expects. The end date is inclusive.
    start_enrollment_period_timestamp: enrollmentStartToDate(
      enrollment.start_enrollment_period_utc,
      data.value?.timezone
    ),
    end_enrollment_period_timestamp: enrollment.end_enrollment_period_utc
      ? enrollmentEndToDate(enrollment.end_enrollment_period_utc, data.value?.timezone)
      : undefined,
    doesntEnd: !enrollment.end_enrollment_period_utc,
  }
}

function cancelEdit() {
  editingId.value = null
  editEntry.value = null
  markedForDeletion.value = false
}

function submitDelete() {
  if (!editingId.value) return
  emit('delete-existing', { id: editingId.value, onSuccess: cancelEdit })
}

function toggleEditDoesntEnd(entry: ProgramEnrollmentEntry) {
  entry.doesntEnd = !entry.doesntEnd
  if (entry.doesntEnd) {
    entry.end_enrollment_period_timestamp = undefined
  }
  editFormRef.value?.validateField('end_enrollment_period_timestamp')
}

function submitEdit() {
  if (!editingId.value || !editEntry.value || !isEditEntryValid.value) return
  emit('edit-existing', {
    id: editingId.value,
    body: {
      start_enrollment_period_timestamp: editEntry.value.start_enrollment_period_timestamp!,
      end_enrollment_period_timestamp: editEntry.value.doesntEnd
        ? null
        : editEntry.value.end_enrollment_period_timestamp!,
      site_split: editEntry.value.site_split!,
    },
    onSuccess: cancelEdit,
  })
}

// --- Opt-out periods (existing enrollments only — a not-yet-created enrollment has no id) ---
const addingOptOutPeriodFor = ref<number | null>(null)
const newOptOutRange = ref<[string, string] | undefined>()
// Enrollments whose opt-out periods list should stop trusting the (now stale) embedded
// snapshot and fetch from the dedicated endpoint instead — set once a period is added.
const optOutPeriodsDirty = reactive(new Set<number>())
// Keyed by enrollment id, so the "add" form below can read whatever periods that
// enrollment's OptOutPeriodsList currently has loaded to check for overlaps.
const optOutPeriodsListRefs = ref<Record<number, InstanceType<typeof OptOutPeriodsList>>>({})

function optOutControlsDisabled(enrollment: IProgramEnrollment) {
  if (props.disabled) return true
  if (editingId.value !== null && editingId.value !== enrollment.id) return true
  return editingId.value === enrollment.id && markedForDeletion.value
}

function startAddOptOutPeriod(enrollmentId: number) {
  addingOptOutPeriodFor.value = enrollmentId
  newOptOutRange.value = undefined
}

function cancelAddOptOutPeriod() {
  addingOptOutPeriodFor.value = null
  newOptOutRange.value = undefined
}

function resetTransientState() {
  cancelEdit()
  cancelAddOptOutPeriod()
  optOutPeriodsDirty.clear()
}

const newOptOutRangeUtc = computed(() => {
  if (!newOptOutRange.value || !data.value?.timezone) return null
  const [start, end] = newOptOutRange.value
  const tz = data.value.timezone
  const startUtc = DateTime.fromISO(start, { zone: tz }).toUTC()
  const endUtc = DateTime.fromISO(end, { zone: tz }).toUTC()
  if (!startUtc.isValid || !endUtc.isValid || endUtc <= startUtc) return null
  return { startUtc, endUtc }
})

// Mirrors the BE's own checks (range order, hour alignment is enforced by the picker's
// value-format, and containment within the enrollment's period) so bad input is caught
// before a round-trip to the API.
const isNewOptOutRangeValid = computed(() => {
  if (!newOptOutRangeUtc.value || !addingOptOutPeriodFor.value) return false
  const { startUtc, endUtc } = newOptOutRangeUtc.value

  const enrollment = props.existingEnrollments?.find((e) => e.id === addingOptOutPeriodFor.value)
  if (!enrollment) return false

  const enrollmentStart = DateTime.fromISO(enrollment.start_enrollment_period_utc)
  const enrollmentEnd = enrollment.end_enrollment_period_utc
    ? DateTime.fromISO(enrollment.end_enrollment_period_utc)
    : null

  if (startUtc < enrollmentStart) return false
  if (enrollmentEnd && endUtc > enrollmentEnd) return false

  return true
})

// Real-time check against whatever opt-out periods are already loaded for this enrollment —
// only a fraction of all existing periods (the list is paginated), but still a useful
// early warning. The BE re-validates the full set on submit regardless.
// Doubles as both the disabled flag and the tooltip message.
const optOutRangeOverlapMessage = computed(() => {
  if (!newOptOutRangeUtc.value || !addingOptOutPeriodFor.value) return undefined
  const { startUtc, endUtc } = newOptOutRangeUtc.value

  const loadedPeriods = optOutPeriodsListRefs.value[addingOptOutPeriodFor.value]?.loadedPeriods ?? []
  const overlaps = loadedPeriods.some((period) => {
    const periodStart = DateTime.fromISO(period.opt_out_start_utc)
    const periodEnd = DateTime.fromISO(period.opt_out_end_utc)
    return startUtc < periodEnd && periodStart < endUtc
  })

  return overlaps ? 'This range overlaps with an existing opt-out period.' : undefined
})

function submitAddOptOutPeriod(enrollmentId: number) {
  const tz = data.value?.timezone
  if (!isNewOptOutRangeValid.value || !tz) return
  const [start, end] = newOptOutRange.value!
  const body: ICreateOptOutPeriodBody = {
    opt_out_start_timestamp: DateTime.fromISO(start, { zone: tz }).toUTC().toISO()!,
    opt_out_end_timestamp: DateTime.fromISO(end, { zone: tz }).toUTC().toISO()!,
  }
  emit('create-opt-out-period', {
    programEnrollmentId: enrollmentId,
    body,
    onSuccess: () => {
      optOutPeriodsDirty.add(enrollmentId)
      cancelAddOptOutPeriod()
    },
  })
}
</script>

<template>
  <div class="enrollment-form flex flex-col gap-6">
    <el-form class="flex flex-col gap-6">
      <el-form-item label="Site">
        <RcSiteSelect v-model="model.site" :disabled="props.disabled || isEditMode" />
        <span v-if="isLoading" class="text-xs text-text-tertiary">Loading...</span>
        <span v-if="isError" class="text-xs text-text-tertiary"
          >Error fetching site's ISO: {{ error?.message ?? "That's all we know" }}
        </span>
        <div v-if="data" class="flex gap-1">
          <span class="text-text-tertiary">ISO: </span>
          <span class="text-grayscale-300">{{ data?.iso }}</span>
        </div>
      </el-form-item>
    </el-form>

    <!-- Existing enrollments (read-only, editable inline) -->
    <div v-if="existingEnrollments?.length" class="flex flex-col gap-4">
      <label class="font-semibold">Current Programs</label>
      <el-card
        v-for="enrollment in existingEnrollments"
        :key="enrollment.id"
        :class="{ 'marked-for-deletion': editingId === enrollment.id && markedForDeletion }"
      >
        <template #header>
          <span class="max-w-4/5">{{ enrollment.program.name }}</span>
          <!-- Editing this card: trash toggles pending deletion, pen otherwise enters edit mode -->
          <el-button
            v-if="editingId === enrollment.id"
            link
            :icon="Delete"
            :class="{ 'text-[var(--text-badge-orange)]': markedForDeletion }"
            :disabled="props.disabled"
            @click="markedForDeletion = !markedForDeletion"
          />
          <el-button v-else link :disabled="props.disabled || editingId !== null" @click="startEdit(enrollment)">
            <Icon icon="material-symbols:edit-outline" :height="16" />
          </el-button>
        </template>

        <!-- Edit mode -->
        <div v-if="editingId === enrollment.id && editEntry" class="flex flex-col gap-4">
          <el-form
            ref="editFormRef"
            :hide-required-asterisk="true"
            :validate-on-rule-change="false"
            :model="editEntry"
            :rules="getEnrollmentRules(editEntry)"
            label-position="top"
            class="flex flex-col gap-4"
          >
            <div class="grid grid-cols-[minmax(0,3fr)_minmax(0,1fr)] gap-4">
              <el-form-item label="Program Type">
                <span class="text-sm">{{ enrollment.program.type }}</span>
              </el-form-item>
              <el-form-item label="Site Split" prop="site_split">
                <el-input v-model="editEntry.site_split" v-maska="siteSplitOptions" :disabled="editControlsDisabled">
                  <template #suffix>%</template>
                </el-input>
              </el-form-item>
            </div>

            <div class="grid grid-cols-3 gap-3">
              <el-form-item label="Effective Date" prop="start_enrollment_period_timestamp">
                <el-date-picker
                  v-model="editEntry.start_enrollment_period_timestamp"
                  placeholder="MM/DD/YYYY"
                  format="MM/DD/YYYY"
                  value-format="YYYY-MM-DD"
                  clearable
                  :suffix-icon="Calendar"
                  :disabled="editControlsDisabled"
                  @change="revalidateEndDate(editFormRef, editEntry)"
                />
              </el-form-item>

              <el-form-item label="End Date" prop="end_enrollment_period_timestamp">
                <el-date-picker
                  v-model="editEntry.end_enrollment_period_timestamp"
                  placeholder="MM/DD/YYYY"
                  format="MM/DD/YYYY"
                  value-format="YYYY-MM-DD"
                  clearable
                  :suffix-icon="Calendar"
                  :disabled="editEntry.doesntEnd || editControlsDisabled"
                />
              </el-form-item>

              <el-form-item class="flex items-end pb-2">
                <el-checkbox
                  v-model="editEntry.doesntEnd"
                  label="No End Date"
                  class="h-4 mr-0 flex items-center gap-1"
                  :disabled="editControlsDisabled"
                  @click.prevent="toggleEditDoesntEnd(editEntry)"
                />
              </el-form-item>
            </div>
          </el-form>

          <span v-if="markedForDeletion" class="text-sm text-[var(--text-badge-orange)]">
            This program will be removed. Click Delete to confirm, or the Cancel button to keep it.
          </span>

          <div class="flex justify-end gap-2">
            <el-button link :disabled="props.disabled" @click="cancelEdit">Cancel</el-button>
            <el-button
              v-if="markedForDeletion"
              type="danger"
              :disabled="props.disabled"
              :loading="props.disabled"
              @click="submitDelete"
              >Delete</el-button
            >
            <el-button
              v-else
              type="primary"
              :disabled="!isEditEntryValid || props.disabled"
              :loading="props.disabled"
              @click="submitEdit"
              >Update</el-button
            >
          </div>
        </div>

        <!-- Read-only view -->
        <div v-else class="flex flex-col gap-4">
          <div class="grid grid-cols-[minmax(0,3fr)_minmax(0,1fr)] gap-4 text-sm">
            <div>
              <div class="text-xs text-text-tertiary mb-1">Program Type</div>
              {{ enrollment.program.type }}
            </div>
            <div>
              <div class="text-xs text-text-tertiary mb-1">Site Split</div>
              {{ enrollment.site_split }}%
            </div>
          </div>
          <div class="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div class="text-xs text-text-tertiary mb-1">Effective Date</div>
              {{ formatEnrollmentDate(enrollment.start_enrollment_period_utc, data?.timezone) }}
            </div>
            <div>
              <div class="text-xs text-text-tertiary mb-1">End Date</div>
              {{
                enrollment.end_enrollment_period_utc
                  ? formatEnrollmentEndDate(enrollment.end_enrollment_period_utc, data?.timezone)
                  : '—'
              }}
            </div>
            <div v-if="!enrollment.end_enrollment_period_utc" class="flex items-end">
              <span class="text-xs text-text-tertiary">No End Date</span>
            </div>
          </div>
        </div>

        <!-- Opt-out periods -->
        <div class="flex flex-col gap-3 pt-3 mt-3 border-t border-[var(--el-border-color-lighter)]">
          <div class="flex items-center justify-between">
            <label class="text-xs text-text-tertiary">Opt-Out Periods</label>
            <span
              class="text-[var(--text-brand)] font-medium text-xs"
              :class="optOutControlsDisabled(enrollment) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
              @click="!optOutControlsDisabled(enrollment) && startAddOptOutPeriod(enrollment.id)"
              >+ Add opt-out period</span
            >
          </div>

          <OptOutPeriodsList
            :ref="
              (el: any) => {
                if (el) {
                  optOutPeriodsListRefs[enrollment.id] = el as InstanceType<typeof OptOutPeriodsList>
                } else {
                  delete optOutPeriodsListRefs[enrollment.id]
                }
              }
            "
            :enrollment="enrollment"
            :disabled="optOutControlsDisabled(enrollment)"
            :timezone="data?.timezone"
            :force-refresh="optOutPeriodsDirty.has(enrollment.id)"
            @delete-opt-out-period="emit('delete-opt-out-period', $event)"
          />
          <div v-if="addingOptOutPeriodFor === enrollment.id" class="flex flex-col gap-2">
            <el-date-picker
              v-model="newOptOutRange"
              type="datetimerange"
              range-separator="to"
              start-placeholder="Start date & time"
              end-placeholder="End date & time"
              format="YYYY-MM-DD HH:00"
              value-format="YYYY-MM-DDTHH:00:00"
              popper-class="opt-out-period-picker-popper"
              :class="{ 'has-overlap': optOutRangeOverlapMessage }"
              style="width: 100%"
              :show-confirm="false"
              :disabled="optOutControlsDisabled(enrollment)"
            />
            <span v-if="!data?.timezone" class="text-xs text-[var(--text-badge-orange)]"
              >Site must have a timezone set before adding an opt-out period.</span
            >
            <span v-else-if="newOptOutRange && !isNewOptOutRangeValid" class="text-xs text-[var(--text-badge-orange)]"
              >Selected range must fall within the enrollment's period and end after it starts.</span
            >
            <span v-else-if="optOutRangeOverlapMessage" class="text-xs text-[var(--text-badge-orange)]">{{
              optOutRangeOverlapMessage
            }}</span>
            <div class="flex justify-end gap-2">
              <el-button link :disabled="props.disabled" @click="cancelAddOptOutPeriod">Cancel</el-button>
              <el-button
                type="primary"
                :disabled="
                  !isNewOptOutRangeValid ||
                  props.disabled ||
                  optOutControlsDisabled(enrollment) ||
                  !!optOutRangeOverlapMessage
                "
                :loading="props.disabled"
                @click="submitAddOptOutPeriod(enrollment.id)"
                >Add</el-button
              >
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <div class="flex flex-col gap-6">
      <label class="font-semibold">Programs</label>

      <div v-for="(entry, index) in model.enrollments" :key="entry.data_key" class="flex flex-col gap-4">
        <el-card>
          <template #header>
            Program {{ index + 1 }}
            <el-button link :icon="Delete" :disabled="props.disabled" @click="removeEnrollment(index)" />
          </template>

          <el-form
            :ref="
              (el: any) => {
                if (el) enrollmentFormRefs[index] = el as FormInstance
              }
            "
            :hide-required-asterisk="true"
            :validate-on-rule-change="false"
            :model="entry"
            :rules="getEnrollmentRules(entry)"
            label-position="top"
            class="flex flex-col gap-4"
          >
            <!-- Top row -->
            <div class="grid grid-cols-[minmax(0,3fr)_minmax(0,1fr)] gap-4">
              <el-form-item label="Program" prop="program">
                <ProgramSelect v-model="entry.program" :disabled="props.disabled" />
              </el-form-item>
              <el-form-item label="Site Split" prop="site_split">
                <el-input v-model="entry.site_split" v-maska="siteSplitOptions" :disabled="props.disabled">
                  <template #suffix>%</template>
                </el-input>
              </el-form-item>
            </div>

            <!-- Bottom row -->
            <div class="grid grid-cols-3 gap-3">
              <el-form-item label="Effective Date" prop="start_enrollment_period_timestamp">
                <el-date-picker
                  v-model="entry.start_enrollment_period_timestamp"
                  placeholder="MM/DD/YYYY"
                  format="MM/DD/YYYY"
                  value-format="YYYY-MM-DD"
                  clearable
                  :suffix-icon="Calendar"
                  :disabled="props.disabled"
                  @change="revalidateEndDate(enrollmentFormRefs[index], entry)"
                />
              </el-form-item>

              <el-form-item label="End Date" prop="end_enrollment_period_timestamp">
                <el-date-picker
                  v-model="entry.end_enrollment_period_timestamp"
                  placeholder="MM/DD/YYYY"
                  format="MM/DD/YYYY"
                  value-format="YYYY-MM-DD"
                  clearable
                  :suffix-icon="Calendar"
                  :disabled="entry.doesntEnd || props.disabled"
                />
              </el-form-item>

              <el-form-item class="flex items-end pb-2">
                <el-checkbox
                  v-model="entry.doesntEnd"
                  label="No End Date"
                  class="h-4 mr-0 flex items-center gap-1"
                  :disabled="props.disabled"
                  @click.prevent="toggleDoesntEnd(entry, index)"
                />
              </el-form-item>
            </div>
          </el-form>
          <div v-if="describePeriod(entry)" class="mt-3 text-xs text-text-tertiary">{{ describePeriod(entry) }}</div>
        </el-card>
      </div>

      <span
        class="text-[var(--text-brand)] font-medium"
        :class="props.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
        @click="!props.disabled && model.enrollments.push(newEntry())"
        >+ Add program</span
      >
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Light/dark mode card against the form background
.enrollment-form {
  --card-bg: var(--fill-secondary);
}

html.dark .enrollment-form {
  --card-bg: var(--fill-primary);
}

:deep() {
  .el-card {
    border-color: transparent;
    background-color: var(--card-bg);
    box-shadow: none;
  }

  // Component-label token flips grayscale-700 (light) ↔ grayscale-300 (dark).
  .el-form-item__label,
  .el-checkbox__label {
    color: var(--text-component-label);
  }

  // Element Plus absolutely positions validation messages under the field, which lets
  // them spill over whatever follows the form (e.g. the period summary). Keep them in
  // the flow so the row grows to fit instead.
  .el-form-item__error {
    position: static;
    padding-top: 4px;
  }

  .marked-for-deletion {
    border-color: var(--component-stroke-error);
  }

  // Element Plus range inputs use an inset box-shadow rather than a border, so the
  // overlap warning is applied the same way (and needs to win over the hover state).
  .has-overlap.el-input__wrapper,
  .has-overlap.el-input__wrapper:hover {
    box-shadow: 0 0 0 1px var(--component-stroke-error) inset;
  }
}
</style>

<style lang="scss">
// Opt-out periods must be hour-aligned, so the minute/second spinner columns are
// hidden — value-format hardcodes ":00:00" regardless of what's scrolled to.
.opt-out-period-picker-popper {
  .el-time-spinner__wrapper:nth-child(n + 2) {
    display: none;
  }
}
</style>
